// Copyright 2022 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { Router } from "aurelia-router";
import { IWorkflowMode } from "services/WorkflowModes/IWorkflowMode";
import { WorkflowModes } from "services/WorkflowModes/WorkflowModes";
import { WorkflowModeService } from "services/WorkflowModes/WorkflowModeService";

enum WorkflowModeMageSteps {
    MarketSelection = 0,
    CAMSetSelection = 1,
    WorkflowModeSelection = 2,
}

interface Market {
    name: string;
    image: string;
    headingText: string;
    selectableCAMSets: CAMSet[];
}

interface CAMSet {
    name: string;
    image: string;
    headingText: string;
    explanationText: string;
    selectableWorkflowModes: IWorkflowMode[];
}

@autoinject
export class WorkflowModeMage {
    // Step 1 and 2
    private currentStep = WorkflowModeMageSteps.MarketSelection;

    private get isSelectingMarket(): boolean {
        return this.currentStep == WorkflowModeMageSteps.MarketSelection;
    }
    private get isSelectingCAMSet(): boolean {
        return this.currentStep == WorkflowModeMageSteps.CAMSetSelection;
    }
    private get isSelectionWorkflowMode(): boolean {
        return this.currentStep == WorkflowModeMageSteps.WorkflowModeSelection;
    }

    private readonly camSets: CAMSet[] = [
        {
            name: "Sorama CAM64 or CAM1K",
            image: "/resources/images/usecases/acoustic_design_cycle.jpg",
            headingText: "Make sure you have a Sorama CAM64 or CAM1K before we start.",
            explanationText: `
                The Sorama CAM64 and CAM1K are powerful tools for in-depth acoustic analysis.
                They are recognizable by their uniform arrays, meaning that the microphones are 
                evenly spaced out in a rectangular grid. This characteristic makes them suitable 
                for Near Field Holography which allows you to look at sound behavior on top 
                of localization, done with Far Field Beamforming. 
                <br/></br/>
                Selecting this workflow mode will enable; the ‘Streaming’ and ‘Measure’ workflows
                which allow you to collect data with the CAM64 and CAM1K.`,
            selectableWorkflowModes: [WorkflowModes.AcousticDesignCycle],
        },
        {
            name: "Sorama CAM iV64",
            image: "/resources/images/usecases/building_acoustics.jpg",
            headingText: "Make sure you have a Sorama CAM iV64, CAM iV64s or CAM iV64Ex before we start.",
            explanationText: `
                The Sorama CAM iV64 is a portable device with on-board processing to be used in
                the field for sound localization with Far Field Beamforming. Its microphones are 
                placed in a sunflower pattern on the array to increase the accuracy in the higher frequencies. 
                <br/><br/>
                Selecting this workflow mode will give you the best experience of the Portal in order to
                collect- and perform post-processing analysis on your CAM iV64 measurements.`,
            selectableWorkflowModes: [WorkflowModes.AcousticDesignCycleLite],
        },
        {
            name: "Sorama CAM64/1K and CAM iV64",
            image: "/resources/images/usecases/full_mode.jpg",
            headingText: "Make sure you have a Sorama CAM before we start.",
            explanationText: `
                The Sorama CAM64 and CAM1K are powerful tools for in-depth acoustic analysis. 
                They are recognizable by their uniform arrays, meaning that the microphones are 
                evenly spaced out in a rectangular grid. This characteristic makes them suitable 
                for Near Field Holography which allows you to look at sound behavior on top of 
                localization, done with Far Field Beamforming.
                <br/><br/>
                The Sorama CAM iV64 is a portable device with on-board processing to be used in 
                the field for sound localization with Far Field Beamforming. Its microphones are 
                placed in a sunflower pattern on the array to increase the accuracy in the higher frequencies.
                <br/><br/>
                Selecting this workflow mode will enable all workflows for both hardware types. It is recommended
                to first get familiar with the usage of your CAMs with their respective dedicated workflow modes.`,
            selectableWorkflowModes: [WorkflowModes.FullWFM],
        },
    ];

    private readonly industrialCamSets: CAMSet[] = [
        {
            name: "Sorama CAM iV64s or CAM iV64Ex",
            image: "/resources/images/usecases/industrial-camiv64s-ex.png",
            headingText: "",
            explanationText: `Our handheld acoustic camera CAM iV64s is developed for various industrial applications.
            The CAM iV64Ex provides the same functionality and are ATEX certified to perform acoustic inspections in ATEX Zone 2 and Zone 1.
            <br/><br/>
            Detect air and gas leaks, mechanical anomalies, and partial discharges to make industrial areas safer and more efficient.`,
            selectableWorkflowModes: [WorkflowModes.IndustrialHandheld],
        },
        {
            name: "Sorama L642V+ or L642Ex",
            image: "/resources/images/usecases/industrial-l642Vex.png",
            headingText: "",
            explanationText: `Our acoustic monitor L642V+ is developed for various industrial applications.
            The L642Ex provides the same functionality and are ATEX certified to perform acoustic inspections in ATEX Zone 2 and Zone 1.
            <br/><br/>
            Detect air and gas leaks, mechanical anomalies, and partial discharges to make industrial areas safer and more efficient.`,
            selectableWorkflowModes: [WorkflowModes.IndustrialFixed],
        },
    ];

    private readonly markets: Market[] = [
        {
            name: "R&D",
            image: "/resources/images/usecases/r_and_d_market.png",
            headingText:
                "Perform acoustic measurements and implement sound design into your R&D process to develop quieter and better performing products.",
            selectableCAMSets: this.camSets,
        },
        {
            name: "Building Acoustics",
            image: "/resources/images/usecases/building_acoustics_market.png",
            headingText:
                "Find sound and air leaks quick and easy with the Sorama Portal. Improve the acoustic performance of doors, windows, facades and offices.",
            selectableCAMSets: this.camSets,
        },
        {
            name: "Industrial",
            image: "/resources/images/usecases/industrial_market.png",
            headingText:
                "Measure the acoustic performance of your industrial applications and improve safety and quality.",
            selectableCAMSets: this.industrialCamSets,
        },
    ];

    private selectedCAMSets: CAMSet[];
    private selectedCAMSet: CAMSet;
    private selectedWorkflowMode: IWorkflowMode;

    constructor(
        private readonly workflowModeService: WorkflowModeService,
        private readonly router: Router
    ) {
        workflowModeService.resetSelectedWorkflowModeInStorage();
    }

    private selectMarket(market: Market): void {
        // If the market only has the choice of one camSet we might as well select it
        if (market.selectableCAMSets.length === 1) {
            this.selectCAMSet(market.selectableCAMSets[0]);
        }

        this.selectedCAMSets = market.selectableCAMSets;
        this.currentStep++;
    }

    private selectCAMSet(camSet: CAMSet): void {
        // If the camSet only has the choice of one workflowMode we might as well select it
        if (camSet.selectableWorkflowModes.length === 1) {
            this.selectedWorkflowMode = camSet.selectableWorkflowModes[0];
        }

        this.selectedCAMSet = camSet;
        this.currentStep++;
    }

    private back(): void {
        this.currentStep--;

        if (this.selectedCAMSets.length === 1) {
            this.currentStep--;
        }

        this.selectedCAMSet = undefined;
        this.selectedWorkflowMode = undefined;
    }

    private start(): void {
        this.workflowModeService.selectedWorkflowMode = this.selectedWorkflowMode;
        this.router.navigate("/Home");
    }
}
